import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import BookingModal from "./BookingModal";
import ThemeToggle from "./ThemeToggle";

export default function Header() {
  const { lang, t, r, switchLang } = useLang();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: r.rooms, label: t.nav.rooms },
    { to: r.experience, label: t.nav.experience },
    { to: r.offers, label: t.nav.offers },
    { to: r.services, label: t.nav.services },
    { to: r.about, label: t.nav.about },
    { to: r.contact, label: t.nav.contact },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "glass" : "bg-transparent"}`}
      >
        <div className="container-x flex items-center justify-between h-20">
          <Link to={r.home} className="flex items-center gap-3 group" data-testid="logo-link">
            <span className="w-8 h-8 rounded-full border border-[#C5A059] flex items-center justify-center">
              <span className="font-serif text-[#C5A059] text-lg leading-none">A</span>
            </span>
            <div className="leading-tight">
              <div className="font-serif text-base tracking-wide">Hotel Alpi Resort</div>
              <div className="overline text-[10px]">Torino</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-active={location.pathname === l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="nav-link text-sm text-white/80 hover:text-white"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-4">
            <ThemeToggle />
            <button
              data-testid="lang-switch"
              onClick={switchLang}
              className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[#C5A059] transition-colors"
              aria-label="Switch language"
            >
              <span className={lang === "it" ? "text-[#C5A059]" : ""}>IT</span>
              <span className="mx-2 text-white/30">/</span>
              <span className={lang === "en" ? "text-[#C5A059]" : ""}>EN</span>
            </button>
            <button
              data-testid="header-book-button"
              onClick={() => setBookOpen(true)}
              className="hidden sm:inline-flex btn-gold px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-medium rounded-none"
            >
              {t.cta.book}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden glass border-t border-white/10" data-testid="mobile-menu">
            <nav className="container-x py-6 flex flex-col gap-4">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-base text-white/85 hover:text-[#C5A059]"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              ))}
              <button
                onClick={() => setBookOpen(true)}
                className="btn-gold mt-2 py-3 text-xs uppercase tracking-[0.18em]"
                data-testid="mobile-book-button"
              >
                {t.cta.book}
              </button>
            </nav>
          </div>
        )}
      </header>
      <BookingModal open={bookOpen} onOpenChange={setBookOpen} />
    </>
  );
}
